import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useModal, UnsavedChangesModalContent } from "../components/Modal";

const NAVIGATION_BLOCKER_MODAL_ID = "navigation-blocker-unsaved-changes-modal";
const BLOCKER_KEY = "__navBlocker__";

interface UseNavigationBlockerOptions {
  /**
   * Whether navigation should be blocked (e.g., when there are unsaved changes)
   */
  shouldBlock: boolean;
  /**
   * Optional callback when user confirms navigation (discards changes)
   */
  onNavigate?: () => void;
}

/**
 * A hook that blocks navigation when there are unsaved changes.
 * Shows a confirmation modal when the user tries to navigate away.
 */
export const useNavigationBlocker = ({
  shouldBlock,
  onNavigate,
}: UseNavigationBlockerOptions) => {
  const { openModal, closeModal } = useModal();
  const location = useLocation();

  const shouldBlockRef = useRef(shouldBlock);
  const isModalOpenRef = useRef(false);
  const isIntentionalNavigationRef = useRef(false);
  const hasGuardEntryRef = useRef(false);
  const onNavigateRef = useRef(onNavigate);
  const currentPathRef = useRef(location.pathname + location.search);

  // Keep refs in sync
  useEffect(() => {
    shouldBlockRef.current = shouldBlock;
  }, [shouldBlock]);

  useEffect(() => {
    onNavigateRef.current = onNavigate;
  }, [onNavigate]);

  useEffect(() => {
    currentPathRef.current = location.pathname + location.search;
  }, [location.pathname, location.search]);

  // Add/remove guard history entry when shouldBlock changes
  useEffect(() => {
    if (shouldBlock && !hasGuardEntryRef.current) {
      window.history.pushState(
        { [BLOCKER_KEY]: true },
        "",
        currentPathRef.current,
      );
      hasGuardEntryRef.current = true;
    }

    // Cleanup: remove guard entry if shouldBlock becomes false
    if (!shouldBlock && hasGuardEntryRef.current) {
      window.history.back();
      hasGuardEntryRef.current = false;
    }
  }, [shouldBlock]);

  useEffect(() => {
    const handlePopState = () => {
      if (isIntentionalNavigationRef.current) {
        isIntentionalNavigationRef.current = false;
        hasGuardEntryRef.current = false;
        return;
      }

      if (!shouldBlockRef.current) {
        return;
      }

      // If modal is already open, ignore
      if (isModalOpenRef.current) {
        // Re-add guard entry since we're staying
        window.history.pushState(
          { [BLOCKER_KEY]: true },
          "",
          currentPathRef.current,
        );
        return;
      }

      // User clicked back and left our guard entry
      // We're now at the previous page in history, but same URL might still show
      // Push guard entry back to restore position

      window.history.pushState(
        { [BLOCKER_KEY]: true },
        "",
        currentPathRef.current,
      );
      hasGuardEntryRef.current = true;

      // Show modal
      isModalOpenRef.current = true;

      openModal({
        id: NAVIGATION_BLOCKER_MODAL_ID,
        title: "",
        content: (
          <UnsavedChangesModalContent
            onDiscard={() => {
              closeModal(NAVIGATION_BLOCKER_MODAL_ID);
              isModalOpenRef.current = false;
              isIntentionalNavigationRef.current = true;
              onNavigateRef.current?.();
              // Go back twice: once past our guard, once to actually navigate
              window.history.go(-2);
            }}
            onBack={() => {
              closeModal(NAVIGATION_BLOCKER_MODAL_ID);
              isModalOpenRef.current = false;
              // Stay on page - guard is already restored
            }}
          />
        ),
        showCloseButton: false,
        closeOnBackdropClick: false,
        maxWidth: "600px",
        containerPadding: "60px 90px",
        contentPadding: "0",
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [openModal, closeModal]);

  // Handle browser refresh/close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (shouldBlockRef.current) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Manual navigation confirmation
  const confirmNavigation = useCallback(
    (onConfirm: () => void) => {
      if (shouldBlockRef.current) {
        isModalOpenRef.current = true;

        openModal({
          id: NAVIGATION_BLOCKER_MODAL_ID,
          title: "",
          content: (
            <UnsavedChangesModalContent
              onDiscard={() => {
                closeModal(NAVIGATION_BLOCKER_MODAL_ID);
                isModalOpenRef.current = false;
                isIntentionalNavigationRef.current = true;
                // Remove guard entry before navigating
                if (hasGuardEntryRef.current) {
                  window.history.back();
                  hasGuardEntryRef.current = false;
                }
                onNavigateRef.current?.();
                // Use setTimeout to ensure history.back() completes
                setTimeout(() => {
                  onConfirm();
                }, 10);
              }}
              onBack={() => {
                closeModal(NAVIGATION_BLOCKER_MODAL_ID);
                isModalOpenRef.current = false;
              }}
            />
          ),
          showCloseButton: false,
          closeOnBackdropClick: false,
          maxWidth: "600px",
          containerPadding: "60px 90px",
          contentPadding: "0",
        });
      } else {
        onConfirm();
      }
    },
    [openModal, closeModal],
  );

  return {
    confirmNavigation,
  };
};

export default useNavigationBlocker;
